import Form, { FieldWatch } from "@imminently/decisively-ui/Form";
import Select from "@imminently/decisively-ui/Select";
import Switch from "@imminently/decisively-ui/Switch";
import TimeField from "@imminently/decisively-ui/TimeField";
import TimeInput, { TIME_PRESETS } from "@imminently/decisively-ui/TimeInput";
import { formatTime, parseTime } from "@imminently/decisively-ui/dateTimeFormat";
import * as React from "react";
import {
  ComponentPage,
  ComponentPreview,
  ComponentSection,
  ControlPanel,
  ControlRow,
  VariantGrid,
  VariantItem,
} from "../components/ComponentPage";
import {
  CUSTOM_PRESETS,
  DateTimeTestInputs,
  KeyboardReference,
  PRESET_LAYOUT_OPTIONS,
  PRESET_SETS,
  PRESET_SET_OPTIONS,
  STEP_OPTIONS,
  type TestInput,
  resolveStep,
} from "../components/DateTimeTestInputs";

const TIME_TEST_INPUTS: TestInput[] = [
  { input: "9:00", expect: "accept" },
  { input: "9:00PM", expect: "accept" },
  { input: "21:00", expect: "accept" },
  { input: "12:00 AM", expect: "accept", note: "midnight" },
  { input: "12:00 PM", expect: "accept", note: "noon" },
  { input: "11:59:59 pm", expect: "accept" },
  { input: "21:00AM", expect: "reject" },
  { input: "21:61", expect: "reject" },
];

const KEYBOARD_ROWS: [string, string][] = [
  ["Type + Enter / Tab", "Commit the typed time (12 or 24 hour). Invalid text stays visible and is marked invalid."],
  ["ArrowDown / ArrowUp", "Open the list on the current value, then move through the options."],
  ["PageDown / PageUp", "Move four options at a time."],
  ["Home / End", "First / last option in the current column."],
  [
    "ArrowLeft / ArrowRight",
    "With presets beside the times: switch column to the nearest time, once you have moved through the list.",
  ],
  ["Enter", "Pick the highlighted option. If you typed since moving, commits the typed text instead."],
  ["Escape", "Close the list without changing the value."],
];

const HOUR_FORMAT_OPTIONS = [
  { value: "locale", label: "Locale default" },
  { value: "12", label: "12 hour" },
  { value: "24", label: "24 hour" },
];

export function TimePickerPage() {
  const [value, setValue] = React.useState("");
  const [hourFormat, setHourFormat] = React.useState("12");
  const [step, setStep] = React.useState("15");
  const [presets, setPresets] = React.useState("dayBounds");
  const [presetLayout, setPresetLayout] = React.useState<"side" | "stacked">("side");
  const [showSeconds, setShowSeconds] = React.useState(false);
  const [allowNow, setAllowNow] = React.useState(false);
  const [clearable, setClearable] = React.useState(true);
  const [disabled, setDisabled] = React.useState(false);
  const [error, setError] = React.useState(false);

  const hour12 = hourFormat === "locale" ? undefined : hourFormat === "12";

  return (
    <ComponentPage
      title="Time Picker"
      description="TimeInput: a typed time with a keyboard-navigable list of times. Replaces the native time input."
    >
      <ComponentSection
        title="Playground"
        description="Values are 24 hour strings (HH:mm, or HH:mm:ss when seconds are set)."
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview className="flex-col items-stretch gap-2">
            <TimeInput
              aria-label="Time"
              value={value}
              onValueChange={setValue}
              hour12={hour12}
              stepMinutes={resolveStep(step)}
              presets={PRESET_SETS[presets]?.presets}
              presetLayout={presetLayout}
              showSeconds={showSeconds}
              allowNow={allowNow}
              clearable={clearable}
              disabled={disabled}
              error={error}
            />
            <code className="text-xs text-muted-foreground">{value ? JSON.stringify(value) : "empty"}</code>
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Hour format">
              <Select
                value={hourFormat}
                onValueChange={(next) => setHourFormat(next ?? "12")}
                options={HOUR_FORMAT_OPTIONS}
              />
            </ControlRow>
            <ControlRow label="Step">
              <Select
                value={step}
                onValueChange={(next) => setStep(next ?? "15")}
                options={STEP_OPTIONS}
              />
            </ControlRow>
            <ControlRow label="Presets">
              <Select
                value={presets}
                onValueChange={(next) => setPresets(next ?? "none")}
                options={PRESET_SET_OPTIONS}
              />
            </ControlRow>
            <ControlRow label="Preset layout">
              <Select
                value={presetLayout}
                onValueChange={(next) => setPresetLayout(next === "stacked" ? "stacked" : "side")}
                options={PRESET_LAYOUT_OPTIONS}
              />
            </ControlRow>
            <ControlRow label="Show seconds">
              <Switch
                checked={showSeconds}
                onCheckedChange={setShowSeconds}
              />
            </ControlRow>
            <ControlRow label="Allow now">
              <Switch
                checked={allowNow}
                onCheckedChange={setAllowNow}
              />
            </ControlRow>
            <ControlRow label="Clearable">
              <Switch
                checked={clearable}
                onCheckedChange={setClearable}
              />
            </ControlRow>
            <ControlRow label="Disabled">
              <Switch
                checked={disabled}
                onCheckedChange={setDisabled}
              />
            </ControlRow>
            <ControlRow label="Error">
              <Switch
                checked={error}
                onCheckedChange={setError}
              />
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      <ComponentSection
        title="Keyboard"
        description="ARIA combobox: focus stays in the input and the list follows via aria-activedescendant."
      >
        <KeyboardReference rows={KEYBOARD_ROWS} />
      </ComponentSection>

      <ComponentSection title="States">
        <ComponentPreview>
          <VariantGrid columns={4}>
            <VariantItem label="Empty">
              <TimeInput aria-label="Empty" />
            </VariantItem>
            <VariantItem label="12 hour">
              <TimeInput
                aria-label="12 hour"
                value="21:30"
                hour12
              />
            </VariantItem>
            <VariantItem label="24 hour">
              <TimeInput
                aria-label="24 hour"
                value="21:30"
                hour12={false}
              />
            </VariantItem>
            <VariantItem label="Disabled">
              <TimeInput
                aria-label="Disabled"
                value="21:30"
                disabled
              />
            </VariantItem>
          </VariantGrid>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Presets"
        description="Presets are listed above the stepped times and reached with the same keys. Pass any TimePreset[]; time can be a TimeOfDay or text such as 12:30pm. TIME_PRESETS (startOfDay, nineAm, midday, fivePm, endOfDay) are ready-made helpers."
      >
        <ComponentPreview>
          <VariantGrid columns={3}>
            <VariantItem label="TIME_PRESETS helpers">
              <TimeInput
                aria-label="Helper presets"
                presets={PRESET_SETS.all.presets}
                hour12
              />
            </VariantItem>
            <VariantItem label="Consumer-defined">
              <TimeInput
                aria-label="Custom presets"
                presets={CUSTOM_PRESETS}
                hour12
              />
            </VariantItem>
            <VariantItem label="Presets only (stepMinutes={null})">
              <TimeInput
                aria-label="Presets only"
                presets={PRESET_SETS.businessHours.presets}
                stepMinutes={null}
                hour12
              />
            </VariantItem>
          </VariantGrid>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Form field"
        description="TimeField, as used by the interview designer's time control (default / min / max)."
      >
        <ComponentPreview className="items-stretch justify-start">
          <Form
            className="grid w-full max-w-xl gap-4"
            defaultValues={{ start: "09:00", end: "17:30", cutoff: "now" }}
          >
            <>
              <TimeField
                name="start"
                label="Start"
                fullWidth
              />
              <TimeField
                name="end"
                label="End"
                stepMinutes={30}
                fullWidth
              />
              <TimeField
                name="cutoff"
                label="Cutoff"
                helperText="allowNow and showSeconds"
                allowNow
                showSeconds
                fullWidth
              />
              <FieldWatch names={["start", "end", "cutoff"]}>
                {(values) => <code className="text-xs text-muted-foreground">{JSON.stringify(values)}</code>}
              </FieldWatch>
            </>
          </Form>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection title="Test inputs">
        <DateTimeTestInputs
          description="From the ENG-240 risk check. Typing accepts 12 and 24 hour input regardless of the display format."
          inputs={TIME_TEST_INPUTS}
          describe={(input) => {
            const parsed = parseTime(input);
            return parsed
              ? `${formatTime(parsed, { hour12: true })} / ${formatTime(parsed, { hour12: false, showSeconds: true })}`
              : null;
          }}
        />
      </ComponentSection>
    </ComponentPage>
  );
}
